import React, { JSX, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/gratitude-board/id6758602259";

const INVITE_CODE_PATTERN = /^[A-Z0-9]{4,32}$/;

interface InvitePreviewRow {
  name: string | null;
  cover_image_url: string | null;
}

function normalizeInviteCode(value: string | null | undefined): string | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  return INVITE_CODE_PATTERN.test(upper) ? upper : null;
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1)
  );
}

export default function InvitePage(): JSX.Element {
  const params = useParams<{ code?: string }>();
  const code = useMemo(() => normalizeInviteCode(params.code), [params.code]);
  const [boardName, setBoardName] = useState<string | null>(null);
  const [boardCoverUrl, setBoardCoverUrl] = useState<string | null>(null);
  const onIos = useMemo(() => isIos(), []);

  // Fetch the actual board name + cover so the in-page experience matches
  // the rich link preview the recipient saw before they tapped. The same
  // RPC powers `functions/invite/[code].ts` for OG metadata.
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_invite_preview", {
          p_invite_code: code,
        });
        if (cancelled || error) return;
        const rows = (Array.isArray(data) ? data : [data]) as Array<InvitePreviewRow | null>;
        const row = rows[0];
        if (!row) return;
        if (row.name) setBoardName(row.name);
        if (row.cover_image_url) setBoardCoverUrl(row.cover_image_url);
      } catch {
        // Non-fatal — page still renders with generic copy.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  // On iOS, attempt the custom-scheme handoff once on mount. If the app is
  // installed but AASA hasn't finished verifying yet (a few minutes after a
  // fresh install), the universal link falls through to this page; the
  // custom scheme still gets the user into the app.
  useEffect(() => {
    if (!code || !onIos) return;
    const frame = document.createElement("iframe");
    frame.style.display = "none";
    frame.src = `gratitude-board://invite/${code}`;
    document.body.appendChild(frame);
    const cleanup = window.setTimeout(() => frame.remove(), 1500);
    return () => {
      window.clearTimeout(cleanup);
      frame.remove();
    };
  }, [code, onIos]);

  // Try the app first, fall back to the App Store if nothing intercepts.
  //
  // Pattern: fire the custom scheme, arm a short timer, and listen for the
  // page becoming hidden. If the app is installed, iOS switches away from
  // Safari — `document.hidden` flips true, we clear the timer, and the
  // fallback never runs. If the app is NOT installed, Safari stays visible,
  // the timer fires, and we redirect to the App Store.
  //
  // The visibility check inside the timer is a second guard: on some iOS
  // versions the page stays "visible" for a frame while the scheme resolves,
  // so this prevents redirecting users who DO have the app.
  const handleOpenInApp = () => {
    if (!code) return;
    const appScheme = `gratitude-board://invite/${code}`;
    const fallbackDelayMs = 1200;

    window.location.href = appScheme;

    const fallbackTimer = window.setTimeout(() => {
      if (document.hidden) return;
      window.location.href = APP_STORE_URL;
    }, fallbackDelayMs);

    const onVisibilityChange = () => {
      if (document.hidden) {
        window.clearTimeout(fallbackTimer);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
  };

  // Deterministic rotation so the pinned cover feels placed, not perfect.
  // Seed off the invite code so the rotation is stable for the life of the
  // page — depending on `boardName` would visibly re-rotate the polaroid
  // when the RPC result arrives. Hook lives above the early return to
  // satisfy the rules of hooks.
  const rotation = useMemo(() => {
    const seed = (code ?? "").length % 5;
    return [-2.4, -1.2, 0.8, 1.6, 2.2][seed];
  }, [code]);

  if (!code) {
    return (
      <div style={pageStyle}>
        <main style={contentStyle}>
          <BrandRow />
          <h1 style={titleStyle}>This invite link looks broken</h1>
          <p style={subtitleStyle}>
            Ask whoever shared it to send the original link again.
          </p>
        </main>
      </div>
    );
  }

  const displayName = boardName ?? "a gratitude board";
  const displayInitial =
    boardName?.trim().charAt(0).toUpperCase() || "?";

  return (
    <div style={pageStyle}>
      <main style={contentStyle}>
        <BrandRow />

        {/* The board, pinned to cork. This IS the hero — no extra card. */}
        <div style={corkFrameStyle} aria-hidden>
          <div style={corkInnerStyle}>
            <div
              style={{
                ...polaroidStyle,
                transform: `rotate(${rotation}deg)`,
              }}
            >
              {boardCoverUrl ? (
                <img src={boardCoverUrl} alt="" style={polaroidImageStyle} />
              ) : (
                <div style={polaroidPlaceholderStyle}>
                  <span style={polaroidInitialStyle}>{displayInitial}</span>
                </div>
              )}
              <div style={tapeStyle} />
            </div>
          </div>
        </div>

        <h1 style={titleStyle}>{displayName}</h1>
        <p style={subtitleStyle}>
          {boardName
            ? "You're invited to join."
            : "You've been invited to join a board."}
        </p>

        {onIos ? (
          <>
            <button
              type="button"
              onClick={handleOpenInApp}
              style={primaryButtonStyle}
            >
              Open in Gratitude Board
            </button>
            <a
              href={APP_STORE_URL}
              style={secondaryButtonStyle}
              target="_blank"
              rel="noopener noreferrer"
            >
              Don't have the app yet? Get it here.
            </a>
          </>
        ) : (
          <a
            href={APP_STORE_URL}
            style={primaryButtonStyle}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Gratitude Board on the App Store
          </a>
        )}
      </main>
    </div>
  );
}

function BrandRow(): JSX.Element {
  return (
    <div style={brandRowStyle}>
      <img src="/og-image.png" alt="" style={brandLogoStyle} />
      <span style={brandWordmarkStyle}>Gratitude Board</span>
    </div>
  );
}

// --- Styles -----------------------------------------------------------------
// Inline styles keep the page self-contained.

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background:
    "radial-gradient(ellipse at top, #fff7ea 0%, #f4e7d4 60%, #e8d6b8 100%)",
  fontFamily:
    "var(--font-main, 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
  color: "var(--text-primary, #1e293b)",
};

const contentStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 380,
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const brandRowStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px 6px 6px",
  backgroundColor: "rgba(255, 252, 244, 0.7)",
  borderRadius: 999,
  marginBottom: 28,
};

const brandLogoStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 6,
  display: "block",
};

const brandWordmarkStyle: React.CSSProperties = {
  fontFamily: "var(--font-hand, 'Caveat', cursive), 'Outfit', sans-serif",
  fontSize: 18,
  fontWeight: 700,
  color: "#3f2e18",
  letterSpacing: 0.2,
};

// --- The cork frame — mirrors the mobile BoardCard DNA ----------------------

const corkFrameStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 280,
  aspectRatio: "1 / 1",
  borderRadius: 6,
  border: "10px solid #8B4513",
  backgroundColor: "#dcb386",
  boxShadow:
    "0 18px 36px -18px rgba(63, 46, 24, 0.4), 0 6px 14px -8px rgba(63, 46, 24, 0.22)",
  marginBottom: 28,
  overflow: "hidden",
  position: "relative",
};

const corkInnerStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 22,
};

const polaroidStyle: React.CSSProperties = {
  width: "82%",
  aspectRatio: "1 / 1",
  backgroundColor: "#ffffff",
  padding: 6,
  boxShadow:
    "0 8px 18px -6px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)",
  position: "relative",
};

const polaroidImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  // Light sepia tone to match the mobile app's vintage photo treatment.
  filter: "sepia(18%) contrast(96%)",
};

const polaroidPlaceholderStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  backgroundColor: "#f4ead4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const polaroidInitialStyle: React.CSSProperties = {
  fontFamily: "var(--font-hand, 'Caveat', cursive), 'Outfit', sans-serif",
  fontSize: 78,
  fontWeight: 700,
  color: "#94a3b8",
  lineHeight: 1,
};

const tapeStyle: React.CSSProperties = {
  position: "absolute",
  top: -10,
  left: "50%",
  marginLeft: -28,
  width: 56,
  height: 18,
  backgroundColor: "rgba(255, 255, 255, 0.55)",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
};

// --- Copy -------------------------------------------------------------------

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-hand, 'Caveat', cursive), 'Outfit', sans-serif",
  fontSize: 44,
  fontWeight: 700,
  color: "#3f2e18",
  margin: "0 0 4px 0",
  lineHeight: 1.1,
  letterSpacing: 0,
  // Long board names should wrap, not overflow.
  wordBreak: "break-word",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 16,
  lineHeight: 1.5,
  color: "var(--text-secondary, #64748b)",
  margin: "0 0 28px 0",
};

// --- CTA --------------------------------------------------------------------

const primaryButtonStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "15px 18px",
  backgroundColor: "#4d97e0",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: 16,
  border: "none",
  borderRadius: 14,
  cursor: "pointer",
  boxShadow: "0 8px 20px -6px rgba(77, 151, 224, 0.45)",
  transition: "transform 0.12s ease, box-shadow 0.12s ease",
};

const secondaryButtonStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "13px 18px",
  marginTop: 12,
  backgroundColor: "transparent",
  color: "var(--text-secondary, #64748b)",
  fontWeight: 600,
  fontSize: 14,
  border: "none",
  borderRadius: 14,
  textDecoration: "underline",
  textDecorationColor: "rgba(100, 116, 139, 0.35)",
  textUnderlineOffset: 3,
  boxSizing: "border-box",
  textAlign: "center",
};

